import { CardElement } from '@stripe/react-stripe-js';

import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormGetValues,
} from 'react-hook-form';
import { countryOptions, states } from '../../../shared/stripe-address-utils';
import { StripeAchPaymentSourceModel } from '../../../api/billing-types';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { useTheme } from '@mui/material/styles';

interface AddCreditCardFormProps {
  control: Control<Partial<StripeAchPaymentSourceModel>>;
  errors: FieldErrors<Partial<StripeAchPaymentSourceModel>>;
  getValues: UseFormGetValues<Partial<StripeAchPaymentSourceModel>>;
  setValue: UseFormSetValue<Partial<StripeAchPaymentSourceModel>>;
  selectedCountry: string;
}

export default function AddCreditCardForm({
  control,
  errors,
  getValues,
  setValue,
  selectedCountry,
}: AddCreditCardFormProps) {
  const theme = useTheme();
  return (
    <Box>
      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid size={{ xs: 12, md: 12 }}>
          <Box
            sx={{
              border: '1px solid',
              borderColor: (theme) =>
                theme.palette.mode === 'light'
                  ? theme.palette.grey[400]
                  : theme.palette.grey[600],
              ':hover': {
                borderColor: (theme) =>
                  theme.palette.mode === 'light'
                    ? theme.palette.grey[700]
                    : theme.palette.grey[300],
              },
              borderRadius: 1,
              p: 1.5,
              mb: 2,
            }}
          >
            <CardElement
              options={{
                style: {
                  base: {
                    fontSize: '16px',
                    fontFamily: 'Roboto, sans-serif',
                    color: theme.palette.text.primary,
                    '::placeholder': {
                      color: theme.palette.text.secondary,
                    },
                  },
                },
              }}
            />
          </Box>
        </Grid>

        <Grid size={{ xs: 12, md: 12 }}>
          <Controller
            name="account_holder_name"
            rules={{
              required: `Please provide a cardholder name.`,
            }}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Cardholder Name"
                error={!!errors.account_holder_name}
                helperText={errors.account_holder_name?.message || ' '}
                size="small"
                fullWidth
                required
              />
            )}
          />
        </Grid>

        <Grid
          size={{ xs: 12, md: 12 }}
          sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}
        >
          <Controller
            name={'address_line1'}
            control={control}
            rules={{
              required: `Please provide a street address.`,
            }}
            render={({ field }) => (
              <TextField
                {...field}
                inputProps={{
                  id: 'address',
                }}
                InputLabelProps={{
                  htmlFor: 'address',
                }}
                label="Street Address"
                fullWidth
                required
                autoComplete="address-line1"
                error={!!errors.address_line1}
                helperText={
                  errors.address_line1 ? errors.address_line1.message : ' '
                }
              />
            )}
          />
        </Grid>

        <Grid
          size={{ xs: 12, md: 6 }}
          sx={{ display: 'flex', flexDirection: 'column' }}
        >
          <Controller
            name={'address_city'}
            control={control}
            rules={{
              required: `Please provide a city.`,
            }}
            render={({ field }) => (
              <TextField
                {...field}
                inputProps={{
                  id: 'city',
                }}
                InputLabelProps={{
                  htmlFor: 'city',
                }}
                label="City"
                fullWidth
                required
                autoComplete="address-level2"
                error={!!errors.address_city}
                helperText={
                  errors.address_city ? errors.address_city.message : ' '
                }
              />
            )}
          />
        </Grid>

        <Grid
          size={{ xs: 12, md: 6 }}
          sx={{ display: 'flex', flexDirection: 'column' }}
        >
          {selectedCountry === 'United States' ? (
            <Controller
              name="address_state"
              control={control}
              rules={{
                required: `Please provide a state.`,
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  inputProps={{
                    id: 'state',
                  }}
                  InputLabelProps={{
                    htmlFor: 'state',
                  }}
                  label="State"
                  fullWidth
                  required
                  autoComplete="address-level1"
                  select
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        style: {
                          maxHeight: 450,
                        },
                      },
                    },
                  }}
                  error={!!errors.address_state}
                  helperText={
                    errors.address_state ? errors.address_state.message : ' '
                  }
                >
                  {states.map((state) => (
                    <MenuItem key={state.value} value={state.value}>
                      {state.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          ) : (
            <Controller
              name="address_state"
              rules={{
                required: `Please provide a province.`,
              }}
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  inputProps={{
                    id: 'state',
                  }}
                  InputLabelProps={{
                    htmlFor: 'state',
                  }}
                  label="Province"
                  fullWidth
                  autoComplete="address-level1"
                  error={!!errors.address_state}
                  helperText={
                    errors.address_state ? errors.address_state.message : ' '
                  }
                />
              )}
            />
          )}
        </Grid>

        <Grid
          size={{ xs: 12 }}
          sx={{ display: 'flex', flexDirection: 'column' }}
        >
          <Controller
            name={'address_country'}
            control={control}
            rules={{
              required: `Please provide a country.`,
            }}
            render={({ field }) => (
              <TextField
                {...field}
                onChange={(e) => {
                  field.onChange(e);

                  if (
                    e.target.value === 'United States' ||
                    getValues('address_country') === 'United States'
                  ) {
                    setValue('address_state', '');
                  }
                }}
                inputProps={{
                  id: 'country',
                }}
                InputLabelProps={{
                  htmlFor: 'country',
                }}
                label="Country/Region"
                fullWidth
                required
                error={!!errors.address_country}
                helperText={
                  errors.address_country ? errors.address_country.message : ' '
                }
                autoComplete="country"
                select
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      style: {
                        maxHeight: 450,
                      },
                    },
                  },
                }}
              >
                {countryOptions.map((country) => (
                  <MenuItem key={country} value={country}>
                    {country}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>

        <Grid
          size={{ xs: 12, md: 12 }}
          sx={{ display: 'flex', flexDirection: 'column', mb: 2 }}
        >
          <Controller
            name={'metadata.nickName'}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                inputProps={{
                  id: 'nickName',
                }}
                InputLabelProps={{
                  htmlFor: 'nickName',
                }}
                label="Nick Name"
                fullWidth
              />
            )}
          />
        </Grid>
      </Grid>
    </Box>
  );
}
